import React, { useState } from "react";

import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { RuleSetAction } from "actions/ruleSets";

import { Button, SecondaryButton } from "components/Form";
import {
  Error,
  SelectField,
  useSubmitHandler,
} from "components/Form/FormikControls";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";

import { ModalTypes } from "constants/navigation";

import {
  getContextByModalType,
  getRules,
  getRuleSetOptionsByRuleBookId,
} from "selectors";

interface MoveToExistingRuleSetModalProps {
  onClose: () => void;
}

interface MoveToExistingRuleSetContext {
  ruleIds: string[];
}

interface FormValues {
  ruleSetId: string | null;
}

interface MoveToExistingRuleSetModalFormProps {
  ruleIds: string[];
  onClose: () => void;
}

const MoveToExistingRuleSetModalForm = ({
  ruleIds,
  onClose,
}: MoveToExistingRuleSetModalFormProps) => {
  const ruleByIdLookup = useSelector(getRules);

  const firstRule = ruleByIdLookup[ruleIds[0]];

  const firstRulesRuleBookId = firstRule.rule_book_id;

  const ruleSetOptions = useSelector(
    getRuleSetOptionsByRuleBookId(firstRulesRuleBookId),
  );

  const selectedRulesBelongToDifferentRuleBooks = ruleIds.some(
    ruleId => ruleByIdLookup[ruleId].rule_book_id !== firstRulesRuleBookId,
  );

  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  useSubmitHandler(isSubmitEnabled, setIsSubmitEnabled);

  return (
    <Form>
      <DialogContent dividers>
        {selectedRulesBelongToDifferentRuleBooks ? (
          <Error>
            The selected rules belong to different Rule Books. Please select
            rules in the same Rule Book.
          </Error>
        ) : (
          <SelectField
            isClearable
            name="ruleSetId"
            label="Rule Set"
            options={ruleSetOptions}
            placeholder="Select Rule Set"
            required
          />
        )}
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={onClose} type="button">
          Cancel
        </SecondaryButton>
        <Button
          data-tour="ruleSetModalSaveButton"
          type="submit"
          disabled={!isSubmitEnabled || selectedRulesBelongToDifferentRuleBooks}
        >
          Save
        </Button>
      </DialogActions>
    </Form>
  );
};

const validationSchema = Yup.object().shape({
  ruleSetId: Yup.string()
    .required("Please select a Rule Set")
    .typeError("Please select a Rule Set"),
});

export function MoveToExistingRuleSetModal(
  props: MoveToExistingRuleSetModalProps,
) {
  const { onClose }: MoveToExistingRuleSetModalProps = props;

  const context =
    useSelector(getContextByModalType(ModalTypes.MoveToExistingRuleSet)) || {};

  const { ruleIds }: MoveToExistingRuleSetContext = context;

  const title = "Add to existing Rule Set";

  const dispatch = useDispatch();

  function onSubmit({ ruleSetId }) {
    dispatch(RuleSetAction.addRulesToExistingRuleSet(ruleIds, ruleSetId));
    onClose();
  }

  const ruleByIdLookup = useSelector(getRules);

  const firstRule = ruleByIdLookup[ruleIds[0]];

  const initialValues: FormValues = { ruleSetId: firstRule.rule_set_id };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth fullScreen>
      <DialogTitle onClose={onClose}>{title}</DialogTitle>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        validateOnMount
      >
        <MoveToExistingRuleSetModalForm ruleIds={ruleIds} onClose={onClose} />
      </Formik>
    </Dialog>
  );
}
